import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Activity, Users, TrendingUp, Filter, Download, Calendar,
  BarChart2, PieChart, Search, X, RefreshCw, Bot
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';
import './UsageDashboard.css';

const UsageDashboard = () => {
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [selectedFeature, setSelectedFeature] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [includeInternal, setIncludeInternal] = useState(true); // Include robert.dong & @jobotics.ai so admin sees own usage (e.g. Vista Analyst)

  const features = [
    { value: 'all', label: 'All Features', icon: Activity },
    { value: 'jobotics_agent', label: 'Jobotics Agent', icon: Bot },
    { value: 'contract_redline', label: 'Contract Redline', icon: BarChart2 },
    { value: 'voice_agent', label: 'Voice Agent', icon: Users },
    { value: 'chat', label: 'Chat', icon: BarChart2 },
    { value: 'vista_analyst', label: 'Vista Analyst', icon: BarChart2 }
  ];

  const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#f59e0b', '#06b6d4', '#ec4899'];

  useEffect(() => {
    fetchData();
  }, [days, selectedFeature, selectedUser, includeInternal]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/usage-dashboard/activities', {
        params: {
          days,
          feature: selectedFeature,
          userEmail: selectedUser,
          includeInternal: includeInternal
        }
      });

      if (response.data.success) {
        setActivities(response.data.activities);
        setSummary(response.data.summary);
        setAvailableUsers(response.data.availableUsers || []);
      } else {
        toast.error('Failed to fetch usage data');
      }
    } catch (error) {
      console.error('Error fetching usage dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Error loading usage data');
      }
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Time', 'User', 'Email', 'Tool', 'Query', 'Success', 'Execution Time (ms)', 'Error Message'];
    const rows = filteredActivities.map(activity => [
      format(parseISO(activity.timestamp), 'yyyy-MM-dd'),
      format(parseISO(activity.timestamp), 'HH:mm:ss'),
      activity.user,
      activity.email || '',
      activity.tool,
      `"${(activity.query || '').replace(/"/g, '""')}"`,
      activity.success ? 'Yes' : 'No',
      activity.executionTime || '',
      activity.errorMessage ? `"${activity.errorMessage.replace(/"/g, '""')}"` : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `usage-dashboard-${days}days-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  // Filter activities by search term
  const filteredActivities = activities.filter(activity => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (activity.user && activity.user.toLowerCase().includes(search)) ||
      (activity.email && activity.email.toLowerCase().includes(search)) ||
      (activity.query && activity.query.toLowerCase().includes(search)) ||
      (activity.tool && activity.tool.toLowerCase().includes(search))
    );
  });

  if (loading && !summary) {
    return (
      <div className="usage-dashboard-loading">
        <RefreshCw className="loading-spinner" size={48} />
        <p>Loading usage data...</p>
      </div>
    );
  }

  return (
    <div className="usage-dashboard">
      <div className="dashboard-header">
        <div className="header-title">
          <Activity size={32} />
          <div>
            <h1>Platform Usage Dashboard</h1>
            <p>Track feature usage and user activity across all tools</p>
          </div>
        </div>

        <div className="header-actions">
          <button onClick={exportToCSV} className="btn-export">
            <Download size={18} />
            Export CSV
          </button>
          <button onClick={fetchData} className="btn-refresh">
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="dashboard-filters">
        <div className="filter-group">
          <Calendar size={18} />
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}>
            <option value="1">Last 24 Hours</option>
            <option value="7">Last 7 Days</option>
            <option value="14">Last 14 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select value={selectedFeature} onChange={(e) => setSelectedFeature(e.target.value)}>
            {features.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <Users size={18} />
          <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
            <option value="all">All Users</option>
            {availableUsers.map(user => (
              <option key={user.email} value={user.email}>{user.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '6px' }}>
          <input
            type="checkbox"
            id="include-internal"
            checked={includeInternal}
            onChange={(e) => setIncludeInternal(e.target.checked)}
            title="Include robert.dong and @jobotics.ai (see your own Vista Analyst usage)"
          />
          <label htmlFor="include-internal" style={{ whiteSpace: 'nowrap', fontSize: '14px' }}>Include internal users</label>
        </div>

        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search activities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <X
              size={18}
              className="clear-search"
              onClick={() => setSearchTerm('')}
            />
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="summary-section">
          <div className="stat-card total">
            <Activity size={24} />
            <div>
              <h3>{summary.totalActivities.toLocaleString()}</h3>
              <p>Total Activities</p>
            </div>
          </div>
          <div className="stat-card users">
            <Users size={24} />
            <div>
              <h3>{summary.uniqueUsers}</h3>
              <p>Active Users</p>
            </div>
          </div>
          <div className="stat-card avg">
            <TrendingUp size={24} />
            <div>
              <h3>{(summary.totalActivities / days).toFixed(0)}</h3>
              <p>Avg per Day</p>
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      {summary && (
        <div className="charts-section">
          {/* Feature Usage Breakdown */}
          <div className="chart-card">
            <h3>Feature Usage Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary.toolStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tool" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#f97316" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Daily Activity Trend */}
          <div className="chart-card">
            <h3>Daily Activity Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={summary.dailyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="total" stroke="#f97316" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Top Users */}
          <div className="chart-card">
            <h3>Top 10 Active Users</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary.userStats} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="user" type="category" width={150} />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Feature Distribution Pie */}
          <div className="chart-card">
            <h3>Feature Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RePieChart>
                <Pie
                  data={summary.toolStats}
                  dataKey="count"
                  nameKey="tool"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.tool}: ${entry.percentage}%`}
                >
                  {summary.toolStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Activities Table */}
      <div className="activities-section">
        <div className="section-header">
          <h2>Recent Activities ({filteredActivities.length})</h2>
          {searchTerm && (
            <span className="search-info">
              Filtered from {activities.length} total
            </span>
          )}
        </div>

        <div className="activities-table-container">
          <table className="activities-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Tool</th>
                <th>Query / Action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {filteredActivities.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    {searchTerm ? 'No activities match your search' : 'No activities found'}
                  </td>
                </tr>
              ) : (
                filteredActivities.map(activity => (
                  <tr key={activity.id}>
                    <td className="timestamp-cell">
                      <div className="timestamp">
                        <span className="date">{format(parseISO(activity.timestamp), 'MMM dd, yyyy')}</span>
                        <span className="time">{format(parseISO(activity.timestamp), 'HH:mm:ss')}</span>
                      </div>
                    </td>
                    <td className="user-cell">
                      <div className="user-info">
                        <span className="user-name">{activity.user}</span>
                        {activity.email && activity.email !== activity.user && (
                          <span className="user-email">{activity.email}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'top', minWidth: 160, maxWidth: 240 }}>
                      {/* Tool labels are sometimes "Base · Specific" (e.g.
                          "Jobotics Agent · AEC Research Agent"). Split so
                          the base name renders as a bold badge on top and
                          the agent suffix sits below it in smaller text —
                          stops the cell from overflowing into Query/Action
                          on long agent names. */}
                      {(() => {
                        const [base, ...rest] = String(activity.tool || '').split(' · ');
                        const suffix = rest.join(' · ');
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span className={`tool-badge tool-${base.toLowerCase().replace(/\s+/g, '-')}`}
                                  style={{ whiteSpace: 'normal', wordBreak: 'break-word', alignSelf: 'flex-start' }}>
                              {base}
                            </span>
                            {suffix && (
                              <span style={{ fontSize: 11, color: '#6b7280', wordBreak: 'break-word', lineHeight: 1.3 }}>
                                {suffix}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="query-cell">
                      <div className="query-text">
                        {activity.query}
                      </div>
                      {activity.response && (
                        <div className="response-preview" style={{ marginTop: 6, fontSize: 12, color: '#4b5563', maxHeight: 80, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                          <strong>Answer:</strong> {activity.response}
                        </div>
                      )}
                    </td>
                    <td className="details-cell">
                      {activity.success !== undefined && (
                        <span className={`detail ${activity.success ? 'success' : 'error'}`}>
                          {activity.success ? '✅ Success' : '❌ Failed'}
                        </span>
                      )}
                      {activity.executionTime && <span className="detail">⏱️ {activity.executionTime}</span>}
                      {activity.errorMessage && (
                        <div className="error-message">
                          <strong>Error:</strong> {activity.errorMessage}
                        </div>
                      )}
                      {activity.metadata && activity.metadata.fileInfo && (
                        <div className="file-info">
                          <strong>File:</strong> {activity.metadata.fileInfo.fileName || 'N/A'}
                          {activity.metadata.fileInfo.fileSize && (
                            <span> ({Math.round(activity.metadata.fileInfo.fileSize / 1024)} KB)</span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsageDashboard;
